import React, { useEffect, useState, useCallback, useRef, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Grid from "@mui/material/Grid2";
import { Box, Button, Paper, Typography, Snackbar, Alert, TextField, Divider, Grid2 } from "@mui/material";
import { Iconify } from "../../../Minimal/src/components/iconify/iconify";
import AddProductDialog from "../component/AddProductDialog";
import BarcodeScanner from "../../../common/BarcodeScanner";
import { ConfirmDialog } from "../../../common/confirm-dialog";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import CartTable from "../../Epos/component/CartTable";
import CartSummary from "../../Epos/component/CartSummary";
import PromotionSummaryCard from "../../Epos/component/PromotionSummaryCard";
import PromotionsDialog from "../component/PromotionsDialog";
import PromotionDetailsDrawerEditEpos from "../../Promotion/components/promo-details-drawer-edit-epos";
import { getCartPromotions, reconcilePromotionDisplayLists } from "../../../../data/promotions";
import { fetchPlaceOrder, releasePlaceOrderLock, updateInvoice, checkProductPromotion, getProductsList } from "../../../../data/epos";
import { transformCartToPromotionPayload, mergePromotionResponseWithCart, enrichCartItemsWithProductCodes, formatDateForAPI, transformProductDetailsToCart, checkPromotionWithGetProductChanges, applyPromotionCheckTrigger } from "../utils/promotionHelpers";
import { notifyPromotionMessage, PROMOTION_MESSAGE_TRIGGERS } from "../../../../utils/promotionMessageToast";
import { determineItemType, parseQty, allowsDecimalQty, getExVatGoodsValueForTotals, applyStickyBundleDiscountPolicy, createStickyBundleDiscountState, resetStickyBundleDiscountState } from "../../Epos/utils/cartCalculations";
import { getTelecallerProductDisplayName } from "../../Epos/utils/telecallerStockLimit";
import { getStockCapForCartLineItem, isQuantityWithinLineStock } from "../../Promotion/utils/promotionLineStock";
import {
  syncCartQtyValidationMap,
  computeCartQuantityChange,
  applyCartQtyValidationResult,
  getCartLineValidationKey,
} from "../../Epos/utils/cartQtyValidation";
import {
  EMPTY_SALES_LIMIT_DIALOG,
  buildStockLimitDialogState,
  buildPurchaseLimitDialogState,
  validateMaximumOrderQuantity,
  extractMaximumOrderQuantityFields,
  mergeOrderLimitFieldsFromCartLines,
} from "../../common/maximumOrderQuantity";
import SalesStockLimitDialog from "../../common/SalesStockLimitDialog";
import { resolveCheckStockBeforeAddToCart } from "../../../../utils/companySettingsCache";
import { useCompanySettings } from "../../../../contexts/CompanySettingsContext";
import { isCartLineOfferDisplayRole } from "../../Promotion/utils/promotionOfferProductsShared";
import { PREFIX } from "../../../common/Config";
import "../../../common/commonStyles.css";

const PROMOTION_CHECK_DEBOUNCE_MS = 800;
const atrEditAllowsDecimal = PREFIX === "atr";
const lineAllowsDecimal = (item) => atrEditAllowsDecimal || allowsDecimalQty(item);

const promoRoleKeyForCartLine = (entity) => {
    if (!entity) return "standard";
    if (isCartLineOfferDisplayRole(entity)) return "get_or_free";
    const pt = String(
        entity.promoType ??
            entity.promosType ??
            entity.promo_type ??
            entity.promos_type ??
            entity.buyOrGet ??
            ""
    )
        .trim()
        .toUpperCase();
    if (pt === "BUY") return "buy_promo";
    return "standard";
};

const isCartLineMergeableOnProductAdd = (item) =>
    promoRoleKeyForCartLine(item) !== "get_or_free";

const EditNewEpos = () => {
    const { orderId } = useParams();
    const navigate = useNavigate();

    const orderIdForLockRef = useRef(orderId);
    const lockReleasedRef = useRef(false);
    orderIdForLockRef.current = orderId;

    const releaseLock = useCallback((keepalive) => {
        const id = orderIdForLockRef.current;
        if (!id || lockReleasedRef.current) return;
        lockReleasedRef.current = true;
        releasePlaceOrderLock(String(id), { keepalive });
    }, []);

    useEffect(() => {
        lockReleasedRef.current = false;
        lastValidAllotQtyRef.current = new Map();
        stockLimitRevertRef.current = null;
    }, [orderId]);

    useEffect(() => {
        if (!orderId) return undefined;
        const onPageHide = (event) => {
            if (event.persisted) return;
            releaseLock(true);
        };
        window.addEventListener("pagehide", onPageHide);
        return () => {
            window.removeEventListener("pagehide", onPageHide);
            releaseLock(false);
        };
    }, [orderId, releaseLock]);

    useEffect(() => {
        if (!orderId) return undefined;
        window.history.pushState(null, "", window.location.href);
        const onPopState = () => {
            const ok = window.confirm(
                "Leave this order? The order will unlock for other users if you leave."
            );
            if (ok) {
                window.removeEventListener("popstate", onPopState);
                releaseLock(false);
                navigate(-1);
            } else {
                window.history.pushState(null, "", window.location.href);
            }
        };
        window.addEventListener("popstate", onPopState);
        return () => {
            window.removeEventListener("popstate", onPopState);
        };
    }, [orderId, navigate, releaseLock]);

    const [selectedCustomer, setSelectedCustomer] = useState(null);
    const [orderHeader, setOrderHeader] = useState(null);
    const [orderChannel, setOrderChannel] = useState("pos");
    const [totalInvDiscPercent, setTotalInvDiscPercent] = useState(0);
    const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "error" });
    const [stockLimitDialog, setStockLimitDialog] = useState(EMPTY_SALES_LIMIT_DIALOG);
    const [deliveryAddress, setDeliveryAddress] = useState("");
    const [staffNote, setStaffNote] = useState("");
    const [openAddDialog, setOpenAddDialog] = useState(false);
    const [saveConfirmDialog, setSaveConfirmDialog] = useState(false);
    const [cancelConfirmDialog, setCancelConfirmDialog] = useState(false);
    const [deleteItemDialog, setDeleteItemDialog] = useState({ open: false, itemId: null });
    const [cartItems, setCartItems] = useState([]);
    const cartItemsRef = useRef([]);
    const cartQtyValidationRef = useRef(new Map());
    const lastValidAllotQtyRef = useRef(new Map());
    const stockLimitRevertRef = useRef(null);
    const [originalOrderProducts, setOriginalOrderProducts] = useState([]);
    const [customerId, setCustomerId] = useState(null);
    const promotionCheckTimerRef = useRef(null);
    const promotionCheckEpochRef = useRef(0);
    const promotionCheckAbortRef = useRef(null);
    const promotionCheckPendingFlushRef = useRef(false);
    const promotionBootstrappedKeyRef = useRef(null);
    const promotionCheckInProgressRef = useRef(false);
    const promotionCheckMetaRef = useRef({ trigger: null, changedProductId: null });
    const orderHeaderRef = useRef(null);
    const customerIdRef = useRef(null);
    const promotionChannelRef = useRef(null);
    const totalInvDiscPercentRef = useRef(0);
    const originalOrderProductsRef = useRef([]);
    const [promotionSummary, setPromotionSummary] = useState(null);
    const [promotionsModalOpen, setPromotionsModalOpen] = useState(false);
    const [promotionDrawerOpen, setPromotionDrawerOpen] = useState(false);
    const [selectedPromotion, setSelectedPromotion] = useState(null);
    const [promotionRefreshKey, setPromotionRefreshKey] = useState(0);
    const queryClient = useQueryClient();
    const [cartTotals, setCartTotals] = useState({
        boxCount: 0,
        pieceCount: 0,
        subtotal: 0,
        vat: 0,
        stdGoods: 0,
        zeroGoods: 0,
        totalBeforeDiscount: 0,
        discountAmount: 0,
        promoDiscPercent: 0,
        grandTotal: 0,
        promoVatAmount:0,
        promoDiscountAmount:0,
        over_all_discount:0
    });
    const [apiTotals, setApiTotals] = useState(null);
    const allotQtyDebounceTimerRef = useRef(null);
    const lastDiscountCalculationRef = useRef({ totalDiscountAmount: 0, itemsHash: "" });
    const isBarcodeProcessingRef = useRef(false);
    const { settings: companySettings } = useCompanySettings();
    const shouldCheckStockBeforeAddToCart = resolveCheckStockBeforeAddToCart(companySettings);

    const isTelecaller = useMemo(
        () => localStorage.getItem("user_role") === "20",
        []
    );

    const stickyBundleStateRef = useRef(null);
    if (!stickyBundleStateRef.current) {
        stickyBundleStateRef.current = createStickyBundleDiscountState();
    }

    const rawBundleDiscounts = useMemo(
        () => (Array.isArray(apiTotals?.bundle_discounts) ? apiTotals.bundle_discounts : []),
        [apiTotals]
    );

    const { effectiveBundleDiscounts, bundleLineDiscountByKey } = useMemo(() => {
        if (!isTelecaller || rawBundleDiscounts.length === 0) {
            return {
                effectiveBundleDiscounts: rawBundleDiscounts,
                bundleLineDiscountByKey: null,
            };
        }

        const stickyResult = applyStickyBundleDiscountPolicy(
            rawBundleDiscounts,
            cartItems,
            stickyBundleStateRef.current
        );

        return {
            effectiveBundleDiscounts: stickyResult.bundleDiscounts,
            bundleLineDiscountByKey: stickyResult.lineDiscountByKey,
        };
    }, [rawBundleDiscounts, cartItems, isTelecaller]);

    const revertCartAllotQty = useCallback((cartItem) => {
        if (!cartItem) return;
        const lineKey = getCartLineValidationKey(cartItem);
        const revertQty = lastValidAllotQtyRef.current.get(lineKey);
        if (revertQty === undefined) return;
        const itemId = cartItem.id || cartItem.product_id;
        const allotStr = lineAllowsDecimal(cartItem)
            ? Number(revertQty).toFixed(2)
            : String(Math.round(revertQty));
        cartQtyValidationRef.current.set(lineKey, revertQty);
        setCartItems((prev) => {
            const next = prev.map((item) => {
                if (item.id === itemId || item.product_id === itemId) {
                    return { ...item, allot_qty: allotStr };
                }
                return item;
            });
            cartItemsRef.current = next;
            return next;
        });
    }, []);

    const openStockLimitDialog = useCallback((sourceItem, capFloor) => {
        stockLimitRevertRef.current = { cartItem: sourceItem };
        setStockLimitDialog(buildStockLimitDialogState(sourceItem, capFloor));
    }, []);

    const openPurchaseLimitDialog = useCallback((sourceItem, message) => {
        stockLimitRevertRef.current = { cartItem: sourceItem };
        setStockLimitDialog(buildPurchaseLimitDialogState(sourceItem, message));
    }, []);

    const closeStockLimitDialog = useCallback(() => {
        const pending = stockLimitRevertRef.current;
        if (pending?.cartItem) {
            revertCartAllotQty(pending.cartItem);
        }
        stockLimitRevertRef.current = null;
        setStockLimitDialog(EMPTY_SALES_LIMIT_DIALOG);
    }, [revertCartAllotQty]);

    const editEposOrderQueryKey = useMemo(() => ["editEposOrder", orderId], [orderId]);

    const { data: orderData, isLoading, isError } = useQuery({
        queryKey: editEposOrderQueryKey,
        queryFn: () => fetchPlaceOrder(orderId),
        enabled: !!orderId,
    });

    const promotionChannel = useMemo(() => {
        if (orderHeader?.channel != null && orderHeader?.channel !== "") {
            return String(orderHeader.channel);
        }
        const userRole = localStorage.getItem("user_role");
        if (userRole === "20") {
            return "5";
        }
        if (userRole === "18") {
            return "4";
        }
        return orderChannel || "4";
    }, [orderChannel, orderHeader]);

    const applyPromotionCheckResponse = useCallback((data, trigger = null) => {
        const hasItems = Array.isArray(data?.data?.items);
        if (data?.success === false || localStorage.getItem("user_role") === "20") {
            notifyPromotionMessage(
                data,
                data?.success === false ? {} : { trigger }
            );
        }
        if (hasItems) {
            const currentCartItems = cartItemsRef.current;
            const cartDataForMerge = {
                data: currentCartItems.length > 0
                    ? currentCartItems
                    : transformProductDetailsToCart(originalOrderProducts),
                success: true
            };
            let mergedItems = mergePromotionResponseWithCart(cartDataForMerge, data, originalOrderProducts, currentCartItems);
            mergedItems = enrichCartItemsWithProductCodes(mergedItems, originalOrderProducts);
            cartItemsRef.current = mergedItems;
            setCartItems(mergedItems);
            lastValidAllotQtyRef.current = syncCartQtyValidationMap(mergedItems, {
                qtyField: "allot",
            });

            if (data?.data?.totals) {
                setApiTotals({
                    ...data.data.totals,
                    bundle_discounts: data.data.bundle_discounts
                });
            }

            if (data?.data?.promotion_summary) {
                setPromotionSummary(data.data.promotion_summary);
            } else if (data?.data?.applied_promotions && Array.isArray(data.data.applied_promotions)) {
                setPromotionSummary({
                    applied_promotions: data.data.applied_promotions,
                    available_promotions: data.data.available_promotions || data.data.applicable_promotions || []
                });
            } else {
                setPromotionSummary(null);
            }
        }
    }, [originalOrderProducts]);

    const checkPromotionMutation = useMutation({
        mutationFn: async (payload) => {
            promotionCheckInProgressRef.current = true;
            promotionCheckAbortRef.current?.abort();
            const abortController = new AbortController();
            promotionCheckAbortRef.current = abortController;
            const epochAtStart = promotionCheckEpochRef.current;
            try {
                const data = await checkProductPromotion(payload, { signal: abortController.signal });
                return { data, epochAtStart, aborted: false, trigger: payload?.trigger ?? null };
            } catch (error) {
                if (error?.name === "AbortError") {
                    return { data: null, epochAtStart, aborted: true, trigger: payload?.trigger ?? null };
                }
                throw error;
            } finally {
                if (promotionCheckAbortRef.current === abortController) {
                    promotionCheckAbortRef.current = null;
                }
            }
        },
        onSuccess: (result) => {
            if (result?.aborted) return;
            const data = result?.data;
            const epochAtStart = result?.epochAtStart;
            if (epochAtStart !== promotionCheckEpochRef.current) {
                return;
            }
            applyPromotionCheckResponse(data, result?.trigger ?? null);
        },
        onError: () => {
            setSnackbar({ open: true, message: "Failed to check promotions", severity: "error" });
        },
        onSettled: () => {
            promotionCheckInProgressRef.current = false;
            if (promotionCheckPendingFlushRef.current) {
                promotionCheckPendingFlushRef.current = false;
                schedulePromotionCheckRef.current?.();
            }
        },
    });

    const schedulePromotionCheckRef = useRef(null);

    const buildOrderDateTime = (dateString) => {
        if (!dateString) {
            const now = new Date();
            const hours = String(now.getHours()).padStart(2, "0");
            const minutes = String(now.getMinutes()).padStart(2, "0");
            const seconds = String(now.getSeconds()).padStart(2, "0");
            return {
                date: formatDateForAPI(now),
                time: `${hours}:${minutes}:${seconds}`,
            };
        }
        if (typeof dateString === "string" && dateString.includes(" ")) {
            const parts = dateString.split(" ");
            return {
                date: parts[0],
                time: parts[1] || "00:00:00",
            };
        }
        const d = new Date(dateString);
        const hours = String(d.getHours()).padStart(2, "0");
        const minutes = String(d.getMinutes()).padStart(2, "0");
        const seconds = String(d.getSeconds()).padStart(2, "0");
        return {
            date: formatDateForAPI(d),
            time: `${hours}:${minutes}:${seconds}`,
        };
    };

    const executePromotionCheck = useCallback((promotionOverride = null, meta = null) => {
        const activeCustomerId = customerIdRef.current;
        if (!activeCustomerId) return;

        if (promotionCheckInProgressRef.current || checkPromotionMutation.isPending) {
            promotionCheckPendingFlushRef.current = true;
            return;
        }

        const currentCartItems = cartItemsRef.current;
        const originalProducts = originalOrderProductsRef.current;
        if (currentCartItems.length === 0 && (!originalProducts || originalProducts.length === 0)) {
            return;
        }

        promotionCheckInProgressRef.current = true;
        promotionCheckEpochRef.current += 1;

        const { date: orderDate, time: orderTime } = buildOrderDateTime(orderHeaderRef.current?.date);
        const orderIdForPayload = orderHeaderRef.current?.order_id ?? orderHeaderRef.current?.id ?? null;
        const cartDataToUse = {
            data:
                currentCartItems.length > 0
                    ? currentCartItems
                    : transformProductDetailsToCart(originalProducts),
            success: true,
        };
        const payload = transformCartToPromotionPayload(
            cartDataToUse,
            activeCustomerId,
            orderDate,
            promotionChannelRef.current,
            orderTime,
            orderIdForPayload
        );
        payload.over_all_discount = totalInvDiscPercentRef.current ?? 0;
        if (promotionOverride) {
            payload.promotion = promotionOverride;
        }

        const triggerMeta = meta || promotionCheckMetaRef.current || {};
        let trigger = triggerMeta.trigger || null;
        let changedProductId = triggerMeta.changedProductId || null;
        if (promotionOverride && !trigger) {
            trigger = PROMOTION_MESSAGE_TRIGGERS.APPLY_PROMOTION;
            changedProductId = null;
        }
        applyPromotionCheckTrigger(payload, trigger, changedProductId);
        promotionCheckMetaRef.current = { trigger: null, changedProductId: null };

        checkPromotionMutation.mutate(payload);
    }, [checkPromotionMutation]);

    const schedulePromotionCheck = useCallback((meta = null) => {
        if (!customerIdRef.current) return;
        if (meta && typeof meta === "object") {
            const prev = promotionCheckMetaRef.current || {};
            if (meta.trigger === PROMOTION_MESSAGE_TRIGGERS.ADD_PRODUCT) {
                promotionCheckMetaRef.current = {
                    trigger: PROMOTION_MESSAGE_TRIGGERS.ADD_PRODUCT,
                    changedProductId: meta.changedProductId ?? prev.changedProductId,
                };
            } else if (
                meta.trigger === PROMOTION_MESSAGE_TRIGGERS.QTY_CHANGE &&
                prev.trigger !== PROMOTION_MESSAGE_TRIGGERS.ADD_PRODUCT
            ) {
                promotionCheckMetaRef.current = {
                    trigger: PROMOTION_MESSAGE_TRIGGERS.QTY_CHANGE,
                    changedProductId: meta.changedProductId ?? prev.changedProductId,
                };
            } else if (!prev.trigger && meta.trigger) {
                promotionCheckMetaRef.current = {
                    trigger: meta.trigger,
                    changedProductId: meta.changedProductId ?? null,
                };
            } else if (meta.changedProductId != null && !prev.trigger) {
                promotionCheckMetaRef.current = {
                    trigger: prev.trigger,
                    changedProductId: meta.changedProductId,
                };
            }
        }
        if (promotionCheckTimerRef.current) {
            clearTimeout(promotionCheckTimerRef.current);
        }
        promotionCheckTimerRef.current = setTimeout(() => {
            promotionCheckTimerRef.current = null;
            executePromotionCheck();
        }, PROMOTION_CHECK_DEBOUNCE_MS);
    }, [executePromotionCheck]);

    schedulePromotionCheckRef.current = schedulePromotionCheck;

    const { data: cartPromotionsData, isLoading: isLoadingCartPromotions } = useQuery({
        queryKey: ["cartPromotions", customerId, promotionRefreshKey, promotionChannel],
        queryFn: () => getCartPromotions(promotionChannel, promotionChannel, customerId),
        enabled: !!customerId && promotionSummary == null && !checkPromotionMutation.isPending,
        staleTime: 1000 * 60,
        gcTime: 1000 * 60 * 5,
        refetchOnWindowFocus: false,
        refetchOnMount: false,
    });

    const updateInvoiceMutation = useMutation({
        mutationFn: updateInvoice,
        onSuccess: (data) => {
            setSaveConfirmDialog(false);
            if (data?.success) {
                setSnackbar({ open: true, message: "Invoice updated successfully", severity: "success" });
                setTimeout(() => {
                    navigate('/sales/newsales');
                }, 1000);
            } else {
                setSnackbar({ open: true, message: data?.message || "Failed to update invoice", severity: "error" });
            }
        },
        onError: (error) => {
            setSnackbar({ open: true, message: error.message || "Failed to update invoice", severity: "error" });
        },
    });

    const getItemDetails = useCallback((item) => {
        return determineItemType(item);
    }, []);

    const calculateProportionalInvoiceDiscount = useCallback((items, totalDiscountAmount) => {
        if (!items || items.length === 0 || totalDiscountAmount === 0) {
            return items.map(item => ({ ...item, invoiceDiscountAmount: 0 }));
        }

        let totalContribution = 0;
        const itemContributions = items.map((item) => {
            const price = parseFloat(item.price) || 0;
            const qty = parseQty(item, item.qty);
            const discount = parseFloat(item.discount || 0);
            const vatRate = parseFloat(item.rate || item.tax_rate || 0);
            const taxMethod = item.tax_method || "exclusive";
            const isVatInclusive = taxMethod === "inclusive";

            const itemDiscountType = item.discount_type || item.productWiseDiscountType || "amount";
            const normalizedDiscountType = String(itemDiscountType).trim().toLowerCase();
            const isFixedDiscount = normalizedDiscountType === "fixed" || normalizedDiscountType === "£" || normalizedDiscountType === "amount";

            let baseTotal = price * qty;

            let discountedTotal;
            if (isFixedDiscount) {
                const discountAmount = parseFloat(discount) || 0;
                discountedTotal = Math.max(0, baseTotal - discountAmount);
            } else {
                const multiplier = Math.min(Math.max(discount, 0), 100);
                discountedTotal = baseTotal * (1 - multiplier / 100);
            }

            let subtotal;
            let vat;
            if (isVatInclusive && vatRate > 0) {
                subtotal = discountedTotal / (1 + vatRate / 100);
                vat = discountedTotal - subtotal;
            } else {
                subtotal = discountedTotal;
                vat = subtotal > 0 ? (subtotal * vatRate) / 100 : 0;
            }

            const itemTotalBeforeInvoiceDiscount = subtotal + vat;
            totalContribution += itemTotalBeforeInvoiceDiscount;

            return {
                itemId: item.id || item.product_id,
                contribution: itemTotalBeforeInvoiceDiscount
            };
        });

        if (totalContribution === 0) {
            return items.map(item => ({ ...item, invoiceDiscountAmount: 0 }));
        }

        return items.map((item) => {
            const contributionData = itemContributions.find(c =>
                c.itemId === (item.id || item.product_id)
            );

            if (!contributionData) {
                return { ...item, invoiceDiscountAmount: 0 };
            }

            const contributionRatio = contributionData.contribution / totalContribution;
            const proportionalDiscountAmount = totalDiscountAmount * contributionRatio;

            return {
                ...item,
                invoiceDiscountAmount: proportionalDiscountAmount
            };
        });
    }, []);

    const calculateCartTotals = useCallback(() => {
        if (apiTotals && cartItems.length > 0 && cartItems.some(item => item.useBackendCalculations)) {
            let boxCount = 0;
            let pieceCount = 0;
            let stdGoods = 0;
            let zeroGoods = 0;

            cartItems.forEach((item) => {
                const orderType = item.type || item.order_type || "Box";
                const qty = parseQty(item, item.qty);

                if (orderType === "Box") {
                    boxCount += qty;
                } else {
                    pieceCount += qty;
                }

                const vatRate = parseFloat(item.rate || item.tax_rate || 0);
                const itemSubtotalForGoods = getExVatGoodsValueForTotals(item);

                if (itemSubtotalForGoods > 0) {
                    if (vatRate > 0) {
                        stdGoods += itemSubtotalForGoods;
                    } else {
                        zeroGoods += itemSubtotalForGoods;
                    }
                }
            });

            const backendSubtotal = parseFloat(apiTotals.sub_total ?? apiTotals.subtotal ?? 0);
            const vatTotal = cartItems.reduce((sum, item) => {
                const lineVat = parseFloat(item.line_vat ?? item.line_tax ?? item.vat ?? item.tax ?? 0);
                return sum + (Number.isFinite(lineVat) ? Math.max(0, lineVat) : 0);
            }, 0);
            const normalizedVatTotal = parseFloat(vatTotal.toFixed(2));
            const totalBeforeDiscount = backendSubtotal + normalizedVatTotal;

            const discountAmount = Math.max(0, (totalBeforeDiscount * (parseFloat(totalInvDiscPercent) || 0)) / 100);
            const bundleDiscounts = Array.isArray(effectiveBundleDiscounts) ? effectiveBundleDiscounts : [];
            let promoDiscountAmount = 0;
            let promoVatAmount = 0;
            const hasPromotionAppliedOnAnyItem = cartItems.some((it) => {
                const isPromoted = it.is_promoted === "1" || it.is_promoted === 1;
                const promotionApplied = it.promotion_applied === true || it.promotion_applied === "true" || it.promotion_applied === 1 || it.promotion_applied === "1";
                const promoId =
                    it.promo_id ?? it.promoId ?? it.promotion_id ?? it.promotionId ?? it.promosId ?? it.promos_id ?? 0;
                const promoIdNum = Number(promoId);
                return isPromoted || promotionApplied || (Number.isFinite(promoIdNum) && promoIdNum > 0);
            });
            bundleDiscounts.forEach((b) => {
                promoDiscountAmount += parseFloat(b.buy_total_discount || 0) + parseFloat(b.get_total_discount || 0);
                promoVatAmount += parseFloat(b.buy_discount_vat || 0) + parseFloat(b.get_discount_vat || 0);
            });
            if (promoDiscountAmount < 0) {
                promoDiscountAmount = 0;
            }
            if (promoVatAmount < 0) {
                promoVatAmount = 0;
            }
            const gross = parseFloat((stdGoods + zeroGoods + normalizedVatTotal).toFixed(2));
            const hasBundleDiscounts = bundleDiscounts.length > 0;
            const apiGrandParsed = parseFloat(
                apiTotals.grand_total ?? apiTotals.grandTotal ?? NaN
            );
            const hasLineItemDiscount = cartItems.some((it) => {
                const d = parseFloat(it.discount ?? it.item_discount ?? it.manualOverrides?.discount ?? 0);
                return Number.isFinite(d) && d > 0.0001;
            });
            let grandTotal;
            if (hasLineItemDiscount) {
                grandTotal = Math.max(0, gross - discountAmount - promoDiscountAmount - promoVatAmount);
            } else if (Number.isFinite(apiGrandParsed)) {
                grandTotal = Math.max(0, apiGrandParsed);
            } else if (hasBundleDiscounts) {
                grandTotal = Math.max(0, gross - discountAmount - promoDiscountAmount - promoVatAmount);
            } else {
                grandTotal = Math.max(0, parseFloat(apiTotals.grand_total ?? apiTotals.grandTotal ?? 0) || 0);
            }
            const promoDiscPercent =
                totalBeforeDiscount > 0
                    ? (promoDiscountAmount / totalBeforeDiscount) * 100
                    : 0;

            setCartTotals({
                boxCount,
                pieceCount,
                subtotal: backendSubtotal,
                vat: normalizedVatTotal,
                stdGoods,
                zeroGoods,
                gross,
                totalBeforeDiscount,
                discountAmount,
               promoDiscountAmount,
                promoVatAmount,
                promoDiscPercent,
                grandTotal,
                over_all_discount: parseFloat(apiTotals.over_all_discount ?? apiTotals.overAllDiscount ?? 0) || 0,
            });
            return;
        }

        let boxCount = 0;
        let pieceCount = 0;
        let stdGoods = 0;
        let zeroGoods = 0;
        let subtotalAmount = 0;
        let vatAmount = 0;

        cartItems.forEach((item) => {
            const orderType = item.type || item.order_type || "Box";
            const qty = parseQty(item, item.qty);

            if (orderType === "Box") {
                boxCount += qty;
            } else {
                pieceCount += qty;
            }

            if (item.useBackendCalculations) {
                const taxMethod = item.tax_method || "exclusive";
                const isVatInclusive = taxMethod === "inclusive";
                const lineSubtotal = parseFloat(item.line_subtotal || item.subtotal || item.sub_total || 0);
                const lineVat = parseFloat(item.line_vat || item.line_tax || item.vat || item.tax || 0);
                const vatRate = parseFloat(item.rate || item.tax_rate || 0);

                if (isVatInclusive) {
                    subtotalAmount += lineSubtotal;
                    vatAmount += lineVat;
                } else {
                    subtotalAmount += lineSubtotal;
                    vatAmount += lineVat;
                }

                const itemSubtotalForGoods = isVatInclusive ? (lineSubtotal - lineVat) : lineSubtotal;
                if (itemSubtotalForGoods > 0) {
                    if (vatRate > 0) {
                        stdGoods += itemSubtotalForGoods;
                    } else {
                        zeroGoods += itemSubtotalForGoods;
                    }
                }
            } else {
                const price = parseFloat(item.price) || 0;
                const discount = parseFloat(item.discount || 0);
                const vatRate = parseFloat(item.rate || item.tax_rate || 0);
                const taxMethod = item.tax_method || "exclusive";
                const isVatInclusive = taxMethod === "inclusive";

                const itemDiscountType = item.discount_type || item.productWiseDiscountType || "amount";
                const normalizedDiscountType = String(itemDiscountType).trim().toLowerCase();
                const isFixedDiscount = normalizedDiscountType === "fixed" || normalizedDiscountType === "£" || normalizedDiscountType === "amount";

                let baseTotal = price * qty;

                let discountedTotal;
                if (isFixedDiscount) {
                    const discountAmount = parseFloat(discount) || 0;
                    discountedTotal = Math.max(0, baseTotal - discountAmount);
                } else {
                    const multiplier = Math.min(Math.max(discount, 0), 100);
                    discountedTotal = baseTotal * (1 - multiplier / 100);
                }

                let itemSubtotal;
                let itemVat;
                if (isVatInclusive && vatRate > 0) {
                    itemSubtotal = discountedTotal / (1 + vatRate / 100);
                    itemVat = discountedTotal - itemSubtotal;
                } else {
                    itemSubtotal = discountedTotal;
                    itemVat = itemSubtotal > 0 ? (itemSubtotal * vatRate) / 100 : 0;
                }

                subtotalAmount += itemSubtotal;
                vatAmount += itemVat;

                if (itemSubtotal > 0) {
                    if (vatRate > 0) {
                        stdGoods += itemSubtotal;
                    } else {
                        zeroGoods += itemSubtotal;
                    }
                }
            }
        });

        const totalBeforeDiscount = subtotalAmount + vatAmount;
        const totalInvoiceDiscountAmount = (totalBeforeDiscount * totalInvDiscPercent) / 100;
        const gross = totalBeforeDiscount;
    
        const grandTotal = parseFloat(apiTotals?.grand_total ?? apiTotals?.grandTotal ?? 0);

        setCartTotals({
            boxCount,
            pieceCount,
            subtotal: subtotalAmount,
            vat: vatAmount,
            stdGoods,
            zeroGoods,
            gross,
            totalBeforeDiscount,
            discountAmount: totalInvoiceDiscountAmount,
            promoDisc: 0,
            promoVat: 0,
            promoDiscPercent: 0,
            grandTotal
        });
    }, [cartItems, totalInvDiscPercent, apiTotals, effectiveBundleDiscounts]);

    const handleQuantityChange = useCallback((cartItem, value, changeType = 'absolute') => {
        const itemId = cartItem.id || cartItem.product_id;

        setCartItems((prev) => {
            const sourceItem = prev.find(item => (item.id === itemId || item.product_id === itemId));
            if (!sourceItem) return prev;

            const currentQty = parseQty(sourceItem, sourceItem.qty);
            let delta;

            if (changeType === 'increment' || changeType === 'decrement') {
                delta = value;
            } else {
                const targetQty = parseQty(sourceItem, value);
                if (!Number.isFinite(targetQty)) return prev;
                delta = targetQty - currentQty;
                if (delta === 0) return prev;
            }

            const isOfferProduct = sourceItem.generated === true || sourceItem.generated === "1" || sourceItem.generated === 1;
            const promosId = sourceItem.promosId || sourceItem.promoId || 0;
            const getQuantity = sourceItem.get_quantity ? parseInt(sourceItem.get_quantity) : null;

            if (isOfferProduct && getQuantity !== null && delta > 0) {
                const totalOfferQty = prev.reduce((sum, item) => {
                    const itemIsOffer = item.generated === true || item.generated === "1" || item.generated === 1;
                    const itemPromosId = item.promosId || item.promoId || 0;

                    if (itemIsOffer && itemPromosId === promosId) {
                        if (item.id === itemId || item.product_id === itemId) {
                            return sum + parseQty(item, item.qty) + delta;
                        } else {
                            return sum + parseQty(item, item.qty);
                        }
                    }
                    return sum;
                }, 0);

                if (totalOfferQty > getQuantity) {
                    setSnackbar({
                        open: true,
                        message: `Cannot increase quantity. Total offer products quantity (${totalOfferQty}) exceeds the maximum allowed (${getQuantity}).`,
                        severity: "warning"
                    });
                    return prev;
                }
            }

            return prev.map((item) => {
                if (item.id === itemId || item.product_id === itemId) {
                    const newQty = parseQty(item, item.qty) + delta;
                    const qtyStr = lineAllowsDecimal(item)
                        ? (Math.round(newQty * 100) / 100).toFixed(2)
                        : String(Math.round(newQty));
                    return {
                        ...item,
                        qty: qtyStr,
                        order_qty: qtyStr
                    };
                }
                return item;
            });
        });
    }, []);

    const handleTypeChange = useCallback((cartItem, newType) => {
        const itemId = cartItem.id || cartItem.product_id;
        setCartItems((prev) => prev.map((item) => {
            if (item.id === itemId) {
                const newPrice = newType === "Box"
                    ? (parseFloat(item.sales_box_price || item.boxPrice || item.price) || 0)
                    : (parseFloat(item.sales_piece_price || item.piecePrice || item.piece_price1) || 0);

                return {
                    ...item,
                    type: newType,
                    order_type: newType,
                    price: newPrice
                };
            }
            return item;
        }));
    }, []);

    const handlePriceChange = useCallback((cartItem, value, options = {}) => {
        const { commit = true } = options;
        const itemId = cartItem.id || cartItem.product_id;
        const parsedValue = commit ? parseFloat(value) : value;
        const numForUnitPrice = Number.isFinite(parseFloat(value)) ? parseFloat(value) : null;

        setCartItems((prev) => prev.map((item) => {
            if (item.id === itemId) {
                const update = { ...item, price: parsedValue };
                if (commit && Number.isFinite(parsedValue)) {
                    update.unit_price = parsedValue;
                } else if (numForUnitPrice !== null) {
                    update.unit_price = numForUnitPrice;
                }
                return update;
            }
            return item;
        }));
    }, []);

    const handlePriceChangeComplete = useCallback(() => {
        if (!customerId) return;
        if (cartItemsRef.current.length === 0) return;
        schedulePromotionCheck();
    }, [customerId, schedulePromotionCheck]);

    const handleDiscountChange = useCallback((cartItem, value, discountType = "amount") => {
        const itemId = cartItem.id || cartItem.product_id;
        const parsedValue = parseFloat(value);
        const discountValue = Number.isFinite(parsedValue) ? parsedValue : 0;
        setCartItems((prev) => prev.map((item) => {
            if (item.id === itemId || item.product_id === itemId) {
                return {
                    ...item,
                    discount: discountValue,
                    item_discount: String(discountValue),
                    discount_type: discountType,
                    productWiseDiscountType: discountType,
                    manualOverrides: {
                        ...(item.manualOverrides || {}),
                        discount: discountValue,
                        discount_type: discountType,
                    },
                };
            }
            return item;
        }));
    }, []);

    const handleDeleteItem = useCallback((itemId) => {
        setCartItems((prev) => {
            const updatedItems = prev.filter((item) => item.id !== itemId && item.product_id !== itemId);

            if (customerId && updatedItems.length > 0) {
                schedulePromotionCheck();
            }

            return updatedItems;
        });
    }, [customerId, schedulePromotionCheck]);

    const clearCart = useCallback(() => {
        resetStickyBundleDiscountState(stickyBundleStateRef.current);
        setCartItems([]);
    }, []);

    useEffect(() => {
        if (!orderData) return;
        if (!orderData?.success) {
            setSnackbar({ open: true, message: orderData?.message || "Failed to load order", severity: "error" });
            return;
        }
        const details = orderData?.details || {};
        const order = Array.isArray(details?.orderDetails) ? details.orderDetails[0] : null;
        const products = Array.isArray(details?.product_details) ? details.product_details : [];

        setOrderHeader(order || null);
        setSelectedCustomer(order ? {
            order_id: order?.id,
            id: order?.customer_id,
            name: order?.name,
            company: order?.company || "",
            address: order?.delivery_address || ""
        } : null);

        if (order?.customer_id) {
            setCustomerId(order.customer_id);
        }

        if (order?.channel) {
            setOrderChannel(order.channel);
        }

        setOriginalOrderProducts(products);

        const orderDiscount = Number(order?.order_discount ?? 0) || 0;
        setTotalInvDiscPercent(orderDiscount);
        setDeliveryAddress(order?.delivery_address || order?.address || "");
        setStaffNote(order?.staff_note || "");
    }, [orderData]);

    useEffect(() => {
        orderHeaderRef.current = orderHeader;
        customerIdRef.current = customerId;
        promotionChannelRef.current = promotionChannel;
        totalInvDiscPercentRef.current = totalInvDiscPercent;
        originalOrderProductsRef.current = originalOrderProducts;
    }, [orderHeader, customerId, promotionChannel, totalInvDiscPercent, originalOrderProducts]);

    useEffect(() => {
        if (!customerId || !originalOrderProducts?.length || !orderHeader) return;

        const bootstrapKey = `${orderId ?? ""}_${customerId}`;

        if (promotionBootstrappedKeyRef.current === bootstrapKey) return;
        promotionBootstrappedKeyRef.current = bootstrapKey;

        setCartItems([]);
        executePromotionCheck(null, { trigger: PROMOTION_MESSAGE_TRIGGERS.PAGE_LOAD });
    }, [customerId, originalOrderProducts, orderHeader, orderId, executePromotionCheck, schedulePromotionCheck]);

    useEffect(() => {
        if (isError) {
            setSnackbar({ open: true, message: "Failed to load order", severity: "error" });
        }
    }, [isError]);

    useEffect(() => {
        cartItemsRef.current = cartItems;
        cartQtyValidationRef.current = syncCartQtyValidationMap(cartItems, {
            qtyField: "allot",
        });
    }, [cartItems]);

    useEffect(() => {
        return () => {
            if (allotQtyDebounceTimerRef.current) {
                clearTimeout(allotQtyDebounceTimerRef.current);
            }
            if (promotionCheckTimerRef.current) {
                clearTimeout(promotionCheckTimerRef.current);
            }
            promotionCheckAbortRef.current?.abort();
        };
    }, []);

    useEffect(() => {
        calculateCartTotals();
    }, [cartItems, totalInvDiscPercent, calculateCartTotals]);

    useEffect(() => {
        const currentItems = cartItemsRef.current;
        if (currentItems.length === 0) {
            if (lastDiscountCalculationRef.current.totalDiscountAmount !== 0) {
                lastDiscountCalculationRef.current = { totalDiscountAmount: 0, itemsHash: "" };
            }
            return;
        }

        const itemsHash = currentItems.map(item =>
            `${item.id || item.product_id}_${item.price}_${item.qty}_${item.discount}_${item.discount_type || item.productWiseDiscountType || 'amount'}`
        ).join('|');

        const totalDiscountAmount = (() => {
            let subtotalAmount = 0;
            let vatAmount = 0;

            currentItems.forEach((item) => {
                const price = parseFloat(item.price) || 0;
                const qty = parseQty(item, item.qty);
                const discount = parseFloat(item.discount || 0);
                const vatRate = parseFloat(item.rate || item.tax_rate || 0);
                const taxMethod = item.tax_method || "exclusive";
                const isVatInclusive = taxMethod === "inclusive";

                const itemDiscountType = item.discount_type || item.productWiseDiscountType || "amount";
                const normalizedDiscountType = String(itemDiscountType).trim().toLowerCase();
                const isFixedDiscount = normalizedDiscountType === "fixed" || normalizedDiscountType === "£" || normalizedDiscountType === "amount";

                let baseTotal = price * qty;

                let discountedTotal;
                if (isFixedDiscount) {
                    const discountAmount = parseFloat(discount) || 0;
                    discountedTotal = Math.max(0, baseTotal - discountAmount);
                } else {
                    const multiplier = Math.min(Math.max(discount, 0), 100);
                    discountedTotal = baseTotal * (1 - multiplier / 100);
                }

                let itemSubtotal;
                let itemVat;
                if (isVatInclusive && vatRate > 0) {
                    itemSubtotal = discountedTotal / (1 + vatRate / 100);
                    itemVat = discountedTotal - itemSubtotal;
                } else {
                    itemSubtotal = discountedTotal;
                    itemVat = itemSubtotal > 0 ? (itemSubtotal * vatRate) / 100 : 0;
                }

                subtotalAmount += itemSubtotal;
                vatAmount += itemVat;
            });

            const totalBeforeDiscount = subtotalAmount + vatAmount;
            return (totalBeforeDiscount * totalInvDiscPercent) / 100;
        })();

        const needsUpdate =
            Math.abs(lastDiscountCalculationRef.current.totalDiscountAmount - totalDiscountAmount) > 0.01 ||
            lastDiscountCalculationRef.current.itemsHash !== itemsHash;

        if (needsUpdate) {
            if (totalDiscountAmount > 0) {
                const updatedItems = calculateProportionalInvoiceDiscount(currentItems, totalDiscountAmount);
                setCartItems(updatedItems);
            } else {
                const hasNonZeroDiscount = currentItems.some(item => parseFloat(item.invoiceDiscountAmount || 0) > 0);
                if (hasNonZeroDiscount) {
                    setCartItems(currentItems.map(item => ({ ...item, invoiceDiscountAmount: 0 })));
                }
            }
            lastDiscountCalculationRef.current = {
                totalDiscountAmount,
                itemsHash
            };
        }
    }, [cartTotals.totalBeforeDiscount, cartTotals.discountAmount, totalInvDiscPercent, calculateProportionalInvoiceDiscount]);

    const handleCancel = () => {
        setCancelConfirmDialog(true);
    };

    const handleSave = () => {
        setSaveConfirmDialog(true);
    };

    const handleCloseSnackbar = () => setSnackbar((s) => ({ ...s, open: false }));

    const showSnackbar = (message, severity = "error") => {
        setSnackbar({ open: true, message, severity });
    };

    const validateQuantity = (value, cartItem = null) => {
        if (!value || value === "") {
            showSnackbar("Please enter a valid quantity");
            return false;
        }
        const allowDecimal = cartItem ? lineAllowsDecimal(cartItem) : atrEditAllowsDecimal;
        const numValue = allowDecimal ? parseFloat(value) : Number(value);
        if (isNaN(numValue) || numValue < 0) {
            showSnackbar("Please enter a valid quantity");
            return false;
        }
        if (allowDecimal) {
            if (numValue < 0.01 || numValue > 9999.99) {
                showSnackbar("Quantity must be between 0.01 and 9999.99");
                return false;
            }
            const decimalPart = String(value).split(".")[1];
            if (decimalPart && decimalPart.length > 2) {
                showSnackbar("Quantity can have at most 2 decimal places");
                return false;
            }
        } else if (!Number.isInteger(numValue) || numValue < 1 || numValue > 9999) {
            showSnackbar("Quantity must be between 1 and 9999");
            return false;
        }
        return true;
    };

    const handleDeleteItemClick = (itemId) => {
        setDeleteItemDialog({ open: true, itemId });
    };

    const handleConfirmDeleteItem = () => {
        if (deleteItemDialog.itemId) {
            handleDeleteItem(deleteItemDialog.itemId);
        }
        setDeleteItemDialog({ open: false, itemId: null });
    };

    const handleCloseDeleteItemDialog = () => {
        setDeleteItemDialog({ open: false, itemId: null });
    };

    const formatDate = (date) => {
        const d = new Date(date);
        const day = String(d.getDate()).padStart(2, '0');
        const month = String(d.getMonth() + 1).padStart(2, '0');
        const year = d.getFullYear();
        return `${day}-${month}-${year}`;
    };

    const formatTime = (date) => {
        const d = new Date(date);
        const hours = String(d.getHours()).padStart(2, '0');
        const minutes = String(d.getMinutes()).padStart(2, '0');
        const seconds = String(d.getSeconds()).padStart(2, '0');
        return `${hours}:${minutes}:${seconds}`;
    };

    const extractDateAndTime = (dateString) => {
        if (!dateString) {
            const now = new Date();
            return {
                date: formatDateForAPI(now),
                time: formatTime(now)
            };
        }

        if (typeof dateString === 'string' && dateString.includes(' ')) {
            const parts = dateString.split(' ');
            const datePart = parts[0];
            const timePart = parts[1] || '00:00:00';
            return {
                date: datePart,
                time: timePart
            };
        }

        const d = new Date(dateString);
        return {
            date: formatDateForAPI(d),
            time: formatTime(d)
        };
    };

    const handleConfirmSave = () => {
        const userInfo = localStorage.getItem("userId");
        const userId = userInfo || "0";

        const currentDate = new Date();
        const orderDate = orderHeader?.order_date || formatDate(currentDate);
        const orderTime = orderHeader?.order_time || formatTime(currentDate);

        const promoTypeMap = new Map();
        cartItems.forEach((item) => {
            const productId = String(item.product_id || item.productId || "");
            const orderType = (item.type || item.order_type || "box").toLowerCase();
            let promoType = item.promo_type || item.promoType || item.promosType || null;
            if (!promoType && item.buyOrGet) {
                const buyOrGetUpper = String(item.buyOrGet).toUpperCase();
                promoType = buyOrGetUpper === "BUY" ? "BUY" : buyOrGetUpper === "GET" ? "GET" : buyOrGetUpper === "FREE" ? "FREE" : null;
            }
            const key = `${productId}_${orderType}`;

            if (promoType === "BUY" || promoType === "GET" || promoType === "FREE") {
                if (!promoTypeMap.has(key)) {
                    promoTypeMap.set(key, new Set());
                }
                promoTypeMap.get(key).add(promoType);
            }
        });

        const orderList = cartItems.map((item) => {
            const qty = parseQty(item, item.qty);
            const price = parseFloat(item.price) || 0;
            const unitPrice = parseFloat(item.unit_price ?? item.price ?? 0);
            const originalPrice = parseFloat(item.original_price ?? item.unit_price ?? price ?? 0);
            const discount = parseFloat(item.discount || 0);
            const vatRate = parseFloat(item.rate || item.tax_rate || 0);
            const taxMethod = item.tax_method || "exclusive";
            const isVatInclusive = taxMethod === "inclusive";

            const isPromotionGenerated = item.generated === true || item.generated === "1" || item.generated === 1;
            const promotionId = item.promoId || item.promosId || null;
            const isPromoted = item.is_promoted === "1" || item.is_promoted === 1;

            const promoDiscountPercentage = parseFloat(item.promo_disc_percentage ?? 0) || 0;
            const promoDiscountAmount = parseFloat(item.promo_disc_amount ?? 0) || 0;

            const itemDiscountType = item.discount_type || item.productWiseDiscountType || "amount";
            const normalizedDiscountType = String(itemDiscountType).trim().toLowerCase();
            const isFixedDiscount = normalizedDiscountType === "fixed" || normalizedDiscountType === "£" || normalizedDiscountType === "amount";

            let finalSubtotal;
            let finalItemVat;
            let productWiseDiscountPercentage;
            let productWiseDiscountAmount;

            const baseTotal = unitPrice * qty;

            let discountedTotal;
            if (isFixedDiscount) {
                const discountAmount = parseFloat(discount) || 0;
                discountedTotal = Math.max(0, baseTotal - discountAmount);
                productWiseDiscountAmount = discountAmount;
                productWiseDiscountPercentage = baseTotal > 0 ? (discountAmount / baseTotal) * 100 : 0;
            } else {
                const multiplier = Math.min(Math.max(discount, 0), 100);
                discountedTotal = baseTotal * (1 - multiplier / 100);
                productWiseDiscountPercentage = discount;
                productWiseDiscountAmount = baseTotal - discountedTotal;
            }

            if (isVatInclusive && vatRate > 0) {
                finalSubtotal = discountedTotal / (1 + vatRate / 100);
                finalItemVat = discountedTotal - finalSubtotal;
            } else {
                finalSubtotal = discountedTotal;
                finalItemVat = finalSubtotal > 0 ? (finalSubtotal * vatRate) / 100 : 0;
            }
            const productId = item.product_id || item.productId || null;
            const productCode = item.code || item.product_code || null;
            const productName = item.name || item.product_name || item.desc || null;
            const size = item.size || null;
            const contractPriceApplied = item.isContractPriceApplied || null;
            const contractPrice = item.ContractPrice || null;

            const orderType = (item.type || item.order_type || "box").toLowerCase();
            let promoType = item.promo_type || item.promoType || item.promosType || null;
            if (!promoType && item.buyOrGet) {
                const buyOrGetUpper = String(item.buyOrGet).toUpperCase();
                promoType = buyOrGetUpper === "BUY" ? "BUY" : buyOrGetUpper === "GET" ? "GET" : buyOrGetUpper === "FREE" ? "FREE" : null;
            }
            const promotionType = promoType;
            const key = `${productId}_${orderType}`;
            const promoTypesForProduct = promoTypeMap.get(key);
            const hasBothBuyAndGet = promoTypesForProduct && promoTypesForProduct.has("BUY") && (promoTypesForProduct.has("GET") || promoTypesForProduct.has("FREE"));
            const isBuyItem = promoType === "BUY";

            const originalProduct = originalOrderProducts.find(
                p => String(p.product_id) === String(productId) &&
                    (p.order_type || "").toLowerCase() === orderType
            );
            let originalProductId = originalProduct?.id || item.original_product_id || null;

            if (hasBothBuyAndGet && isBuyItem) {
                originalProductId = null;
            }

            const id = originalProductId || null;
            let originalOrderQty = null;
            if (item.order_qty !== undefined && item.order_qty !== null && item.order_qty !== "") {
                originalOrderQty = String(item.order_qty);
            } else if (originalProduct?.order_qty !== undefined && originalProduct.order_qty !== null && originalProduct.order_qty !== "") {
                originalOrderQty = String(originalProduct.order_qty);
            } else if (originalProduct?.quantity !== undefined && originalProduct.quantity !== null && originalProduct.quantity !== "") {
                originalOrderQty = String(originalProduct.quantity);
            } else if (originalProduct?.qty !== undefined && originalProduct.qty !== null && originalProduct.qty !== "") {
                originalOrderQty = String(originalProduct.qty);
            }

            return {
                product_id: productId !== null ? String(productId) : null,
                id: id !== null ? String(id) : (hasBothBuyAndGet && isBuyItem ? "0" : null),
                product_code: productCode !== null ? String(productCode) : null,
                product_name: productName !== null ? String(productName) : null,
                unit_price: String(unitPrice.toFixed(2)),
                original_price: String(originalPrice.toFixed(2)),
                quantity: String(qty),
                unit_quantity: String(qty),
                discount: String(discount.toFixed(2)),
                order_type: (item.type || item.order_type || "box").toLowerCase(),
                order_qty: originalOrderQty !== null ? originalOrderQty : String(qty),
                item_tax: String(finalItemVat.toFixed(2)),
                subtotal: String(finalSubtotal.toFixed(2)),
                tax_rate_percent: String(vatRate),
                is_promoted: isPromoted ? "1" : "0",
                size: size !== null ? String(size) : null,
                picked_qty: "0.00",
                accept_qty: String(item.allot_qty !== undefined ? item.allot_qty : qty),
                proforma_qty: "0",
                item_discount: String(discount.toFixed(2)),
                productWiseDiscountType: isFixedDiscount ? "amount" : "percentage",
                productWiseDiscountPercentage: String(productWiseDiscountPercentage.toFixed(2)),
                productWiseDiscountAmount: String(productWiseDiscountAmount.toFixed(2)),
                promoDiscountPercentage: String(promoDiscountPercentage.toFixed(2)),
                promoDiscountAmount: String(promoDiscountAmount.toFixed(2)),
                deliver_qty: "0.00",
                isContractPriceApplied: contractPriceApplied !== null ? String(contractPriceApplied) : "N",
                ContractPrice: contractPrice !== null ? String(contractPrice) : null,
                promo_name: promotionType !== null ? String(promotionType) : null,
                free_product: isPromotionGenerated ? "Y" : "N",
                promo_product: isPromoted ? "Y" : "N",
                promo_id: promotionId !== null ? String(promotionId) : "0",
                promotion_applied: isPromoted ? "1" : "0",
                generated: isPromotionGenerated ? "1" : "0",
            };
        });

        const payload = {
            orderId: orderId,
            status: "Accept",
            group_id: String(orderHeader?.group_id || ""),
            delivery_address: deliveryAddress || "",
            grand_total: String(cartTotals.totalBeforeDiscount?.toFixed(2) || "0.00"),
            over_all_amount: String(cartTotals.discountAmount?.toFixed(2) || "0.00"),
            over_all_discount: String(totalInvDiscPercent || "0.00"),
            orderList: orderList,
            staffNote: staffNote || "",
            order_date: orderDate,
            order_time: orderTime,
            userId: String(userId),
        };

        updateInvoiceMutation.mutate(payload);
    };

    const handleCloseSaveConfirmDialog = () => {
        setSaveConfirmDialog(false);
    };

    const handleConfirmCancel = () => {
        setCancelConfirmDialog(false);
        clearCart();
        setTotalInvDiscPercent(0);
        navigate('/sales/newsales');
    };

    const handleCloseCancelConfirmDialog = () => {
        setCancelConfirmDialog(false);
    };

    const validatePrice = (value, item) => {
        if (!value || value === "") {
            showSnackbar("Please enter a valid price");
            return false;
        }
        if (item?.type === "FREE") {
            showSnackbar("Cannot edit price for free promotional items");
            return false;
        }
        const numValue = Number(value);
        if (isNaN(numValue)) {
            showSnackbar("Please enter a valid price (e.g., 45.99)");
            return false;
        }
        const decimalPlaces = (value.toString().split('.')[1] || '').length;
        if (decimalPlaces > 2) {
            showSnackbar("Please enter a valid price (e.g., 45.99)");
            return false;
        }
        if (numValue < 0.01 || numValue > 9999.99) {
            showSnackbar("Price must be between £0.01 and £9,999.99");
            return false;
        }
        return true;
    };

    const validateDiscount = (value, discountType = "percentage", cartItem = null) => {
        if (value === "" || value === null || value === undefined) {
            return true;
        }

        const numValue = Number(value);

        if (isNaN(numValue)) {
            showSnackbar(discountType === "percentage" ? "Please enter a valid discount percentage" : "Please enter a valid discount amount");
            return false;
        }

        if (numValue < 0) {
            showSnackbar("Discount cannot be negative");
            return false;
        }

        if (discountType === "percentage") {
            if (numValue > 100) {
                showSnackbar("Discount cannot exceed 100%");
                return false;
            }
        } else {
            const isFixed = discountType === "amount" || discountType === "£" || discountType === "fixed";
            if (isFixed && cartItem) {
                const orderType = cartItem.type || cartItem.order_type || "Box";
                const qty = (cartItem.order_qty !== undefined && cartItem.order_qty !== null && cartItem.order_qty !== "")
                    ? parseQty(cartItem, cartItem.order_qty)
                    : parseQty(cartItem, cartItem.qty);
                let unitPrice = 0;
                if (cartItem.unit_price !== undefined && cartItem.unit_price !== null && cartItem.unit_price !== "") {
                    unitPrice = parseFloat(cartItem.unit_price);
                } else if (orderType === "Box") {
                    unitPrice = parseFloat(cartItem.sales_box_price || 0);
                } else {
                    unitPrice = parseFloat(cartItem.sales_piece_price || 0);
                }
                if (unitPrice > 0 && numValue > unitPrice) {
                    showSnackbar(`Discount amount cannot be greater than unit price (£${unitPrice.toFixed(2)})`);
                    return false;
                }
            }
        }

        const decimalPlaces = (value.toString().split('.')[1] || '').length;
        if (decimalPlaces > 2) {
            showSnackbar("Discount can have maximum 2 decimal places");
            return false;
        }

        return true;
    };

    const checkPriceEditPermission = () => {
        const hasPermission = true;
        if (!hasPermission) {
            showSnackbar("You don't have permission to edit prices. Contact your manager.");
            return false;
        }
        return true;
    };

    const handleValidatedQuantityChange = (cartItem, value, changeType = 'absolute') => {
        const sourceItem = cartItem;

        if (changeType !== 'increment' && changeType !== 'decrement' && !validateQuantity(value, cartItem)) {
            return false;
        }

        const result = computeCartQuantityChange({
            sourceItem,
            validationMap: cartQtyValidationRef.current,
            value,
            changeType,
            qtyField: "qty",
            shouldCheckStockBeforeAddToCart,
            shouldSkipStockCheck: () => false,
            cartItems: cartItemsRef.current,
            forceAllowDecimal: atrEditAllowsDecimal,
        });

        const accepted = applyCartQtyValidationResult(result, {
            onRange: (r) => {
                showSnackbar(`Quantity must be between ${r.minQty} and ${r.maxQty}`, "warning");
            },
            onPurchaseLimit: (r) => {
                openPurchaseLimitDialog(sourceItem, r.message);
            },
            onStock: (r) => {
                openStockLimitDialog(sourceItem, r.cap);
            },
        });

        if (!accepted) {
            return false;
        }

        cartQtyValidationRef.current.set(result.lineKey, result.finalQty);
        handleQuantityChange(cartItem, value, changeType);

        if (customerId) {
            schedulePromotionCheck({
                trigger: PROMOTION_MESSAGE_TRIGGERS.QTY_CHANGE,
                changedProductId: cartItem?.product_id || cartItem?.productId || cartItem?.id,
            });
        }
        return true;
    };

    const handleValidatedPriceChange = (cartItem, value, options = {}) => {
        if (!checkPriceEditPermission()) {
            return;
        }
        const itemDetails = cartItem;
        if (itemDetails?.is_promoted === "1" && itemDetails?.type === "FREE") {
            showSnackbar("Cannot edit price for free promotional items");
            return false;
        }

        if (options?.commit === false) {
            handlePriceChange(cartItem, value, options);
            return true;
        }

        if (validatePrice(value, cartItem)) {
            handlePriceChange(cartItem, value, options);
            return true;
        }

        return false;
    };

    const handleValidatedDiscountChange = (cartItem, value, discountType = "amount") => {
        if (validateDiscount(value, discountType, cartItem)) {
            handleDiscountChange(cartItem, value, discountType);
        }
    };

    const handleDiscountChangeComplete = useCallback(() => {
        if (!customerId) return;
        if (cartItemsRef.current.length === 0) return;
        schedulePromotionCheck();
    }, [customerId, schedulePromotionCheck]);

    const handleValidatedTotalInvDiscChange = (value) => {
        if (validateDiscount(value, "percentage", null)) {
            setTotalInvDiscPercent(value);
            schedulePromotionCheck();
        }
    };

    const handleAllotQtyChange = useCallback((cartItem, value) => {
        if (value === "" || value === "." || value === null || value === undefined) {
            return false;
        }

        const numValue = parseFloat(value);
        if (isNaN(numValue) || numValue < 0) {
            showSnackbar("Please enter a valid allot quantity", "error");
            return false;
        }

        const sourceItem = cartItem;
        const result = computeCartQuantityChange({
            sourceItem,
            validationMap: cartQtyValidationRef.current,
            value: numValue,
            changeType: "absolute",
            qtyField: "allot",
            shouldCheckStockBeforeAddToCart,
            shouldSkipStockCheck: () => false,
            cartItems: cartItemsRef.current,
            forceAllowDecimal: atrEditAllowsDecimal,
        });

        const accepted = applyCartQtyValidationResult(result, {
            onRange: (r) => {
                showSnackbar(`Allot Qty must be between ${r.minQty} and ${r.maxQty}`, "error");
            },
            onPurchaseLimit: (r) => {
                openPurchaseLimitDialog(sourceItem, r.message);
            },
            onStock: (r) => {
                openStockLimitDialog(sourceItem, r.cap);
            },
        });

        if (!accepted) {
            revertCartAllotQty(sourceItem);
            return false;
        }

        cartQtyValidationRef.current.set(result.lineKey, result.finalQty);
        lastValidAllotQtyRef.current.set(result.lineKey, result.finalQty);

        const allotValue = lineAllowsDecimal(sourceItem)
            ? Number(result.finalQty).toFixed(2)
            : String(Math.round(result.finalQty));
        const itemId = cartItem.id || cartItem.product_id;
        setCartItems((prev) => prev.map((item) => {
            if (item.id === itemId || item.product_id === itemId) {
                return {
                    ...item,
                    allot_qty: allotValue
                };
            }
            return item;
        }));

        if (customerId) {
            schedulePromotionCheck({
                trigger: PROMOTION_MESSAGE_TRIGGERS.QTY_CHANGE,
                changedProductId: cartItem?.product_id || cartItem?.productId || cartItem?.id,
            });
        }
        return true;
    }, [customerId, showSnackbar, shouldCheckStockBeforeAddToCart, openStockLimitDialog, openPurchaseLimitDialog, revertCartAllotQty, schedulePromotionCheck]);

    const openAddProduct = () => {
        setOpenAddDialog(true);
    };

    const closeAddProduct = () => {
        setOpenAddDialog(false);
    };

    const toNumber = (val) => {
        const n = Number(val);
        return isNaN(n) ? 0 : n;
    };

    const handleAddProductSave = ({ selectedProduct, selectedOrderType, price, quantity }) => {
        const p = toNumber(price) || 0;
        const q = Math.max(1, Math.floor(toNumber(quantity)));
        const orderTypeRaw = selectedOrderType.value;
        const orderType = orderTypeRaw.charAt(0).toUpperCase() + orderTypeRaw.slice(1).toLowerCase();
        const availableQtyForType = orderType === "Piece"
            ? (parseFloat(selectedProduct?.availablePieceQty ?? selectedProduct?.available_piece_qty ?? 0) || 0)
            : (parseFloat(selectedProduct?.availableBoxQty ?? selectedProduct?.available_box_qty ?? 0) || 0);

        const lineForProductStock = {
            ...selectedProduct,
            order_type: orderType,
            type: orderType,
        };

        if (shouldCheckStockBeforeAddToCart && availableQtyForType <= 0) {
            openStockLimitDialog(lineForProductStock, 0);
            return;
        }

        if (q > 9999) {
            setSnackbar({ open: true, message: "Quantity cannot exceed 9999. Please reduce quantity before adding.", severity: "warning" });
            return;
        }

        const prevSnap = cartItemsRef.current;
        const selectedPid = String(selectedProduct.id ?? "");
        const normalizeOrderType = (t) => {
            const s = String(t ?? "Box").trim().toLowerCase();
            return s === "piece" ? "Piece" : "Box";
        };
        const orderTypeNorm = normalizeOrderType(orderType);
        const matchesProductAndOrderType = (item) =>
            String(item.product_id ?? item.productId ?? "") === selectedPid &&
            normalizeOrderType(item.type || item.order_type) === orderTypeNorm;

        const pickLineQty = (item) => {
            const allot = parseFloat(item.allot_qty);
            if (Number.isFinite(allot) && allot >= 0) return allot;
            return parseQty(item, item.qty);
        };

        const existingTotalQtyForStock = prevSnap.reduce((sum, item) => {
            if (!matchesProductAndOrderType(item)) return sum;
            return sum + pickLineQty(item);
        }, 0);
        const targetTotalQtyForStock = existingTotalQtyForStock + q;

        const lineForPurchaseLimit = mergeOrderLimitFieldsFromCartLines(
            lineForProductStock,
            prevSnap
        );
        const existingTotalForPurchase = prevSnap.reduce((sum, item) => {
            if (String(item.product_id ?? item.productId ?? "") !== selectedPid) return sum;
            return sum + pickLineQty(item);
        }, 0);
        const purchaseLimit = validateMaximumOrderQuantity(
            lineForPurchaseLimit,
            existingTotalForPurchase + q
        );
        if (!purchaseLimit.allowed) {
            openPurchaseLimitDialog(lineForPurchaseLimit, purchaseLimit.message);
            return;
        }

        const allowDecForAdd = lineAllowsDecimal(lineForProductStock);
        if (shouldCheckStockBeforeAddToCart && !isQuantityWithinLineStock(lineForProductStock, targetTotalQtyForStock, allowDecForAdd)) {
            const cap = getStockCapForCartLineItem(lineForProductStock);
            openStockLimitDialog(
                lineForProductStock,
                cap != null ? (allowDecForAdd ? cap : Math.floor(cap)) : 0
            );
            return;
        }

        const maxAddQty = allowDecForAdd ? 9999.99 : 9999;
        const existingItem = prevSnap.find(
            (item) => matchesProductAndOrderType(item) && isCartLineMergeableOnProductAdd(item)
        );
        if (existingItem) {
            const currentQty = pickLineQty(existingItem);
            if (currentQty >= maxAddQty) {
                setSnackbar({ open: true, message: `Quantity cannot exceed ${maxAddQty}. Please reduce quantity before adding more.`, severity: "warning" });
                return;
            }
            const newQty = currentQty + q;
            if (newQty > maxAddQty) {
                setSnackbar({ open: true, message: `Quantity cannot exceed ${maxAddQty}. Please reduce quantity before adding more.`, severity: "warning" });
                return;
            }
            const qtyStr = allowDecForAdd
                ? (Math.round(newQty * 100) / 100).toFixed(2)
                : String(Math.round(newQty));
            setCartItems((prev) => {
                const existingItemIndex = prev.findIndex(
                    (item) => matchesProductAndOrderType(item) && isCartLineMergeableOnProductAdd(item)
                );
                if (existingItemIndex === -1) return prev;
                const row = prev[existingItemIndex];
                const updatedRow = {
                    ...row,
                    qty: qtyStr,
                    allot_qty: qtyStr,
                    accept_qty: qtyStr,
                };
                return [updatedRow, ...prev.filter((_, i) => i !== existingItemIndex)];
            });
            if (customerId) {
                schedulePromotionCheck({
                    trigger: PROMOTION_MESSAGE_TRIGGERS.ADD_PRODUCT,
                    changedProductId: selectedProduct?.product_id || selectedProduct?.id,
                });
            }
            closeAddProduct();
            showSnackbar("Product added successfully", "success");
            return;
        }

        setCartItems((prev) => {
            const idGen = `np_${Date.now()}`;
            const boxPrice = Number(selectedProduct.price) || 0;
            const piecePrice = Number(selectedProduct.split_price) || 0;
            const finalBoxPrice = Number(selectedProduct.final_price) || boxPrice;
            const finalPiecePrice = Number(selectedProduct.final_split_price) || piecePrice;
            const split = Number(selectedProduct.split) || 1;
            const vatRate = Number(selectedProduct.rate) || 0;
            const originalPrice = orderType === "Box" ? finalBoxPrice : finalPiecePrice;

            const newItem = {
                id: idGen,
                product_id: selectedProduct.id,
                productId: selectedProduct.id,
                code: selectedProduct.product_code,
                product_code: selectedProduct.product_code,
                name: selectedProduct.product_name,
                product_name: selectedProduct.product_name,
                desc: selectedProduct.product_name,
                type: orderType,
                order_type: orderType,
                split,
                sales_box_price: boxPrice,
                sales_piece_price: piecePrice,
                boxPrice,
                piecePrice,
                qty: String(q),
                order_qty: String(q),
                price: p,
                unit_price: p,
                original_price: originalPrice,
                discount: "0",
                discount_type: "amount",
                rate: String(vatRate),
                tax_rate: String(vatRate),
                is_promoted: "0",
                noOfPieceInBox: split,
                price_1: Number(selectedProduct.price_1 ?? 0) || 0,
                price_2: Number(selectedProduct.price_2 ?? 0) || 0,
                price_3: Number(selectedProduct.price_3 ?? 0) || 0,
                price_4: Number(selectedProduct.price_4 ?? 0) || 0,
                price_5: Number(selectedProduct.price_5 ?? 0) || 0,
                piece_price1: Number(selectedProduct.piece_price1 ?? 0) || 0,
                piece_price2: Number(selectedProduct.piece_price2 ?? 0) || 0,
                piece_price3: Number(selectedProduct.piece_price3 ?? 0) || 0,
                piece_price4: Number(selectedProduct.piece_price4 ?? 0) || 0,
                piece_price5: Number(selectedProduct.piece_price5 ?? 0) || 0,
                cost: Number(selectedProduct.cost ?? 0) || 0,
                itemTax: 0,
                subtotal: 0,
                availableBoxQty: Number(selectedProduct.availableBoxQty ?? 0) || 0,
                availablePieceQty: Number(selectedProduct.availablePieceQty ?? 0) || 0,
                size: selectedProduct.size || "",
                inner_ean_number: selectedProduct.inner_ean_number || "",
                outer_ean_number: selectedProduct.outer_ean_number || "",
                innerEanNumber: selectedProduct.inner_ean_number || "",
                outerEanNumber: selectedProduct.outer_ean_number || "",
                discountedBoxPrice: 0,
                discountedSplitPrice: 0,
                productDiscountApplicable: Number(selectedProduct.productDiscountApplicable ?? 0) || 0,
                promoId: 0,
                promosId: 0,
                promoType: null,
                ...((selectedProduct.categorie_name || selectedProduct.category_name)
                    ? { category_name: String(selectedProduct.categorie_name || selectedProduct.category_name || "").trim() }
                    : {}),
                promosType: null,
                generated: false,
                free_product: "N",
                piece_cost: Number(selectedProduct.piece_cost ?? 0) || 0,
                pieceCost: Number(selectedProduct.piece_cost ?? 0) || 0,
                accept_qty: String(q),
                allot_qty: String(q),
                ...extractMaximumOrderQuantityFields(selectedProduct),
            };

            if (customerId) {
                schedulePromotionCheck({
                    trigger: PROMOTION_MESSAGE_TRIGGERS.ADD_PRODUCT,
                    changedProductId: selectedProduct?.product_id || selectedProduct?.id,
                });
            }

            return [newItem, ...prev];
        });

        closeAddProduct();
        showSnackbar("Product added successfully", "success");
    };

    const handleBarcodeDetected = useCallback(
        async (barcodeValue) => {
            const trimmed = `${barcodeValue}`.trim();
            if (!trimmed || isBarcodeProcessingRef.current) return;
            if (!customerId) {
                setSnackbar({ open: true, message: "Please ensure customer is loaded before scanning", severity: "warning" });
                return;
            }
            isBarcodeProcessingRef.current = true;
            try {
                const response = await getProductsList({
                    pattern: trimmed,
                    customer_id: customerId,
                    orderChannel: orderChannel
                });
                const products = response?.product_details || [];
                const firstProduct = products[0];
                if (!firstProduct) {
                    setSnackbar({ open: true, message: "No product found for scanned code", severity: "warning" });
                    return;
                }
                const split = Number(firstProduct.split) ?? 1;
                let orderTypeValue = "Box";
                if (split === 1) {
                    orderTypeValue = "Piece";
                } else if (split === 2) {
                    orderTypeValue = "Box";
                }
                const selectedOrderType = { id: split === 1 ? 1 : 0, title: orderTypeValue, value: orderTypeValue };
                const boxPrice = Number(firstProduct.final_price ?? firstProduct.price) || 0;
                const piecePrice = Number(firstProduct.final_split_price ?? firstProduct.split_price) || 0;
                const price = orderTypeValue === "Piece" ? piecePrice : boxPrice;
                handleAddProductSave({
                    selectedProduct: firstProduct,
                    selectedOrderType,
                    price: price > 0 ? price.toFixed(2) : "0.00",
                    quantity: "1"
                });
            } catch {
                setSnackbar({ open: true, message: "Unable to fetch product for scanned code", severity: "error" });
            } finally {
                isBarcodeProcessingRef.current = false;
            }
        },
        [customerId, orderChannel, handleAddProductSave]
    );

    const formatPromotionDate = (dateString) => {
        if (!dateString || dateString === "N/A") return dateString;
        try {
            if (dateString.includes("/")) {
                const [day, month, year] = dateString.split("/");
                const date = new Date(year, month - 1, day);
                const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
                return `${day}-${monthNames[date.getMonth()]}`;
            }
            if (dateString.includes("-")) {
                const date = new Date(dateString);
                if (!Number.isNaN(date.getTime())) {
                    const day = String(date.getDate()).padStart(2, "0");
                    return `${day}-${["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"][date.getMonth()]}`;
                }
            }
            return dateString;
        } catch {
            return dateString;
        }
    };

    const getPromotionBanner = (promotion) => {
        if (promotion.type_id === "1") return "Buy X Get Y Free";
        if (promotion.type_id === "2") return "Buy X Get Y Discount";
        if (promotion.type_id === "3") return "Buy X at Discount";
        if (promotion.type_id === "4") return "Multi Layer Special";
        if (promotion.type_id === "5") return "Group Offer";
        return promotion.type || "Special Offer";
    };

    const transformPromotionData = (promotion) => {
        const buyProducts = (promotion.buy_products || []).filter((p) => p.product_name && p.product_name.trim() !== "");
        const offerProducts = (promotion.get_products || promotion.offer_products || []).filter((p) => p.product_name && p.product_name.trim() !== "");
        const allProducts = [...buyProducts, ...offerProducts];
        const typeLabel = getPromotionBanner(promotion);
        return {
            id: String(promotion.id ?? promotion.promotion_id ?? ""),
            title: promotion.title || promotion.name || typeLabel,
            description: promotion.description || "",
            validity:
                promotion.start_date && promotion.end_date
                    ? `${formatPromotionDate(promotion.start_date)} to ${formatPromotionDate(promotion.end_date)}`
                    : "N/A",
            products: allProducts.map((p) => p.product_name || `${p.product_code || ""} - ${p.product_name || ""}`),
            banner: typeLabel,
            buyProducts,
            offerProducts,
            rawData: promotion,
        };
    };

    const appliedPromotionsFromCart =
        cartPromotionsData?.data?.applied_promotions && Array.isArray(cartPromotionsData.data.applied_promotions)
            ? cartPromotionsData.data.applied_promotions.map(transformPromotionData)
            : [];

    const availablePromotionsFromCart = (() => {
        const applicable =
            cartPromotionsData?.data?.applicable_promotions && Array.isArray(cartPromotionsData.data.applicable_promotions)
                ? cartPromotionsData.data.applicable_promotions.map(transformPromotionData)
                : [];
        const appliedIds = appliedPromotionsFromCart.map((p) => String(p.id));
        return applicable.filter((promo) => !appliedIds.includes(String(promo.id)));
    })();

    const transformAPIPromotionSummary = (promo) => {
        const buyProducts = (promo.buy_products || []).filter((p) => p.product_name && p.product_name.trim() !== "");
        const offerProducts = (promo.get_products || promo.offer_products || []).filter((p) => p.product_name && p.product_name.trim() !== "");
        const allProducts = [...buyProducts, ...offerProducts];
        const productsList =
            allProducts.length > 0
                ? allProducts.map((p) => p.product_name || `${p.product_code || ""} - ${p.product_name || ""}`)
                : promo.products || [];
        const validity =
            promo.start_date && promo.end_date
                ? `${formatPromotionDate(promo.start_date)} to ${formatPromotionDate(promo.end_date)}`
                : promo.validity || "N/A";
        const typeLabel = getPromotionBanner(promo);
        return {
            id: String(promo.promotion_id || promo.id || ""),
            title: promo.name || promo.title || typeLabel,
            description: promo.description || `${promo.type || ""} - Savings: £${(promo.savings || 0).toFixed(2)}`,
            validity,
            products: productsList,
            banner: typeLabel,
            buyProducts,
            offerProducts,
            rawData: promo,
        };
    };

    const appliedPromotionsFromAPI = (promotionSummary?.applied_promotions || []).map(transformAPIPromotionSummary);
    const availablePromotionsFromAPI = (promotionSummary?.available_promotions || []).map(transformAPIPromotionSummary);

    const cartHasLines = cartItems.length > 0;
    const hasPromotionSummaryFromCheck = cartHasLines && promotionSummary != null;

    const reconciledPromotionLists = useMemo(() => {
        if (!cartHasLines) {
            return { applied: [], available: [], appliedCount: 0, totalApplicable: 0 };
        }
        if (hasPromotionSummaryFromCheck) {
            return reconcilePromotionDisplayLists(
                appliedPromotionsFromAPI,
                availablePromotionsFromAPI,
                cartItems
            );
        }
        return {
            applied: appliedPromotionsFromCart,
            available: availablePromotionsFromCart,
            appliedCount: cartPromotionsData?.data?.total_applied
                ? parseInt(cartPromotionsData.data.total_applied, 10)
                : appliedPromotionsFromCart.length,
            totalApplicable: cartPromotionsData?.data?.total_applicable
                ? parseInt(cartPromotionsData.data.total_applicable, 10)
                : availablePromotionsFromCart.length + appliedPromotionsFromCart.length,
        };
    }, [
        cartHasLines,
        hasPromotionSummaryFromCheck,
        appliedPromotionsFromAPI,
        availablePromotionsFromAPI,
        cartItems,
        appliedPromotionsFromCart,
        availablePromotionsFromCart,
        cartPromotionsData,
    ]);

    const displayAppliedPromotions = reconciledPromotionLists.applied;
    const displayAvailablePromotions = reconciledPromotionLists.available;
    const promotionCount = reconciledPromotionLists.appliedCount;
    const totalApplicable = reconciledPromotionLists.totalApplicable;

    const handleOpenPromotionsModal = () => setPromotionsModalOpen(true);
    const handleClosePromotionsModal = () => setPromotionsModalOpen(false);
    const handleOpenPromotionDrawer = (promo) => {
        setSelectedPromotion(promo);
        setPromotionDrawerOpen(true);
        setPromotionsModalOpen(false);
    };
    const handleClosePromotionDrawer = () => {
        setPromotionDrawerOpen(false);
        setSelectedPromotion(null);
    };

    const getPromotionProductKeys = (p) => {
        const raw = p?.rawData || p;
        const buyProducts = raw?.buy_products || p?.buyProducts || [];
        const keys = new Set();
        buyProducts.forEach((bp) => {
            if (bp?.product_id != null) keys.add(String(bp.product_id));
            if (bp?.product_code != null && String(bp.product_code).trim() !== "") keys.add(String(bp.product_code).trim());
        });
        return keys;
    };

    const getConflictingAppliedPromotionIds = (promoBeingApplied, appliedList) => {
        const newProductKeys = getPromotionProductKeys(promoBeingApplied);
        if (newProductKeys.size === 0) return [];
        const conflictingIds = [];
        appliedList.forEach((applied) => {
            const appliedId = Number(applied.id);
            const promoId = Number(promoBeingApplied?.id ?? promoBeingApplied?.rawData?.promotion_id);
            if (appliedId === promoId) return;
            const appliedProductKeys = getPromotionProductKeys(applied);
            for (const k of appliedProductKeys) {
                if (newProductKeys.has(k)) {
                    conflictingIds.push(appliedId);
                    break;
                }
            }
        });
        return conflictingIds;
    };

    const handleCheckPromotionGetSwap = useCallback(
        async ({ promotionId, removed, newProducts }) => {
            if (!customerId) {
                return { success: false, message: "Customer is required" };
            }

            const { date: orderDate, time: orderTime } = extractDateAndTime(orderHeader?.date);
            const orderIdForPayload = orderHeader?.order_id ?? orderHeader?.id ?? null;
            const cartDataToUse =
                cartItemsRef.current.length > 0
                    ? cartItemsRef.current
                    : transformProductDetailsToCart(originalOrderProducts);

            promotionCheckEpochRef.current += 1;
            const epochAtStart = promotionCheckEpochRef.current;
            try {
                const data = await checkPromotionWithGetProductChanges({
                    cartItems: cartDataToUse,
                    customerId,
                    channel: promotionChannel,
                    orderDate,
                    orderTime,
                    orderId: orderIdForPayload,
                    promotionId,
                    removedProducts: removed,
                    newProducts,
                    forEdit: true,
                    previewOnly: true,
                });

                if (epochAtStart !== promotionCheckEpochRef.current) {
                    return { success: false, message: "Promotion check was superseded" };
                }
                if (data?.success) {
                    applyPromotionCheckResponse(data);
                    return { success: true };
                }
                return {
                    success: false,
                    message: data?.message || "Failed to update promotion products",
                };
            } catch (error) {
                return {
                    success: false,
                    message: error?.message || "Failed to update promotion products",
                };
            }
        },
        [
            customerId,
            orderHeader,
            originalOrderProducts,
            promotionChannel,
            applyPromotionCheckResponse,
        ]
    );

    const handleRemovePromotion = (promo) => {
        if (!customerId) {
            showSnackbar("Please select a customer first", "error");
            return;
        }
        const promotionId = promo?.id ? Number(promo.id) : promo?.rawData?.id ? Number(promo.rawData.id) : 0;
        if (!promotionId) return;
        const appliedIds = displayAppliedPromotions.map((p) => Number(p.id));
        const applyIds = appliedIds.filter((id) => id !== promotionId);
        const removeIds = [promotionId];
        const { date: orderDate, time: orderTime } = extractDateAndTime(orderHeader?.date);
        const orderIdForPayload = orderHeader?.order_id ?? orderHeader?.id ?? null;
        const cartDataToUse = {
            data: cartItemsRef.current.length > 0 ? cartItemsRef.current : transformProductDetailsToCart(originalOrderProducts),
            success: true,
        };
        const payload = transformCartToPromotionPayload(cartDataToUse, customerId, orderDate, promotionChannel, orderTime, orderIdForPayload);
        payload.promotion = { apply: applyIds, remove: removeIds };
        applyPromotionCheckTrigger(payload, PROMOTION_MESSAGE_TRIGGERS.APPLY_PROMOTION);
        promotionCheckEpochRef.current += 1;
        checkPromotionMutation.mutate(payload, {
            onSuccess: (result) => {
                const data = result?.data;
                const epochAtStart = result?.epochAtStart;
                if (epochAtStart !== undefined && epochAtStart !== promotionCheckEpochRef.current) return;
                if (data?.success) {
                    showSnackbar("Promotion removed successfully", "success");
                    setPromotionRefreshKey((k) => k + 1);
                } else {
                    showSnackbar(data?.message || "Failed to remove promotion", "error");
                }
            },
            onError: () => showSnackbar("Failed to remove promotion. Please try again.", "error"),
        });
    };

    const handleApplyPromotion = async (promo) => {
        if (!customerId) {
            showSnackbar("Please select a customer first", "error");
            return;
        }
        if (cartItems.length === 0) {
            showSnackbar("Please add products to cart first", "error");
            return;
        }
        const newPromoId = Number(promo.id ?? promo.rawData?.id ?? promo.rawData?.promotion_id);
        const removeIds = getConflictingAppliedPromotionIds(promo, displayAppliedPromotions);
        const appliedIds = displayAppliedPromotions.map((p) => Number(p.id));
        const applyIdsAfterRemoval = appliedIds.filter((id) => !removeIds.includes(id));
        const applyIds = applyIdsAfterRemoval.includes(newPromoId) ? applyIdsAfterRemoval : [...applyIdsAfterRemoval, newPromoId];
        const { date: orderDate, time: orderTime } = extractDateAndTime(orderHeader?.date);
        const orderIdForPayload = orderHeader?.order_id ?? orderHeader?.id ?? null;
        const cartDataToUse = {
            data: cartItemsRef.current.length > 0 ? cartItemsRef.current : transformProductDetailsToCart(originalOrderProducts),
            success: true,
        };
        const payload = transformCartToPromotionPayload(cartDataToUse, customerId, orderDate, promotionChannel, orderTime, orderIdForPayload);
        payload.promotion = { apply: applyIds, remove: removeIds };
        applyPromotionCheckTrigger(payload, PROMOTION_MESSAGE_TRIGGERS.APPLY_PROMOTION);
        promotionCheckEpochRef.current += 1;
        try {
            const result = await checkPromotionMutation.mutateAsync(payload);
            const data = result?.data;
            const epochAtStart = result?.epochAtStart;
            if (epochAtStart !== undefined && epochAtStart !== promotionCheckEpochRef.current) return;
            if (data?.success) {
                showSnackbar(`Promotion "${promo.title || promo.rawData?.name || "Promotion"}" applied successfully`, "success");
                setPromotionRefreshKey((k) => k + 1);
            } else {
                showSnackbar(data?.message || "Failed to apply promotion", "error");
            }
        } catch {
            showSnackbar("Failed to apply promotion. Please try again.", "error");
        }
    };

    const isLoadingData = isLoading;

    return (
        <>
            <Box sx={{ p: 1, backgroundColor: '#f5f7fa' }}>
                <Paper variant="elevation" elevation={2} sx={{ p: 1, overflowX: 'hidden' }}>
                    {isLoadingData && (
                        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "300px" }}>
                            <Typography>Loading order details...</Typography>
                        </Box>
                    )}
                    {!isLoadingData && (
                        <>

                            <Grid2 container spacing={2} sx={{
                                display: "flex",
                                flexDirection: { xs: "column", sm: "row", md: "row" },
                                justifyContent: "space-between",
                                alignItems: { xs: "flex-start", sm: "flex-start", md: "center" },
                                gap: { xs: 1, sm: 1, md: 2 }
                            }}>
                                <Grid2 size={{ xs: 12, md: 2}}>
                                    <Typography sx={{ fontSize: "1.25rem", fontWeight: 600 }}>
                                        Edit Invoice
                                    </Typography>
                                </Grid2>
                                <Grid2 size={{ xs: 12, md: 5 }} gap={{ xs: 1, sm: 1, md: 2 }} display="flex" alignItems={{ xs: "flex-start", sm: "flex-start", md: "center" }} direction={{ xs: "column", sm: "column", md: "row" }}>
                                    <Typography sx={{ fontSize: "0.8rem" }}>
                                        <span style={{ fontWeight: 600 }}>Customer Name:</span> {orderHeader?.company || orderHeader?.name || ""}
                                    </Typography>
                                    <Typography sx={{ fontSize: "0.8rem" }}>
                                        <span style={{ fontWeight: 600 }}>Account No:</span> {orderHeader?.account_no || ""}
                                    </Typography>
                                </Grid2>

                                <Grid2 size={{ xs: 12, md: 5 }} display="flex" justifyContent="flex-end" alignItems="center" gap={1} flexWrap="wrap">
                                    <PromotionSummaryCard
                                        promotionCount={promotionCount}
                                        totalApplicable={totalApplicable}
                                        onViewClick={handleOpenPromotionsModal}
                                        disabled={!customerId}
                                    />
                                    <BarcodeScanner
                                        onDetected={handleBarcodeDetected}
                                        debounceInterval={600}
                                        label="Scan to add"
                                    />
                                    <Button
                                        variant="contained"
                                        className="button-variant-contained"
                                        startIcon={<Iconify icon="mdi:plus" width={20} height={20} />}
                                        onClick={openAddProduct}
                                        sx={{
                                            width: { xs: "100%", sm: "100%", md: "auto" }
                                        }}
                                    >
                                        <Typography sx={{ fontSize: "0.8rem", fontWeight: 600 }}>
                                            Add New Product
                                        </Typography>
                                    </Button>
                                </Grid2>
                            </Grid2>
                            <Grid2 size={{ xs: 12, md: 12 }}>
                                <CartTable
                                    cartItems={cartItems}
                                    onTypeChange={handleTypeChange}
                                    onQuantityChange={handleValidatedQuantityChange}
                                    onPriceChange={handleValidatedPriceChange}
                                    onDiscountChange={handleValidatedDiscountChange}
                                    onDeleteItem={handleDeleteItemClick}
                                    showVatAsAmount={true}
                                    isEditMode={true}
                                    onValidationError={showSnackbar}
                                    getItemDetails={getItemDetails}
                                    customerId={selectedCustomer?.id}
                                    isLoading={checkPromotionMutation.isPending}
                                    onAllotQtyChange={handleAllotQtyChange}
                                    onDiscountChangeComplete={handleDiscountChangeComplete}
                                    onPriceChangeComplete={handlePriceChangeComplete}
                                    bundleDiscounts={rawBundleDiscounts}
                                    bundleLineDiscountByKey={bundleLineDiscountByKey}
                                    isTelecaller={isTelecaller}
                                />
                                <CartSummary
                                    cartTotals={cartTotals}
                                    totalInvDiscPercent={totalInvDiscPercent}
                                    onTotalInvDiscChange={handleValidatedTotalInvDiscChange}
                                    isEditMode={true}
                                    hideTotalInvDisc={PREFIX === "atr"}
                                />
                                <Grid container spacing={2} display="flex" flexDirection={{ xs: "column", sm: "column", md: "row" }} alignItems="center" justifyContent="center" mt={2}>
                                    <Grid size={{ xs: 12, md: 4 }}>
                                        <TextField className="input-border text-field" variant="outlined" label="Delivery Address" placeholder="Enter Delivery Address" size="small" fullWidth multiline rows={1} InputLabelProps={{ shrink: true }} value={deliveryAddress} onChange={(e) => setDeliveryAddress(e.target.value)} />
                                    </Grid>
                                    <Grid size={{ xs: 12, md: 4 }}>
                                        <TextField className="input-border text-field" 
                                        variant="outlined" label="Staff Note"
                                         placeholder="Enter Staff Note" 
                                         
                                         size="small" fullWidth multiline rows={1} 
                                         InputLabelProps={{ shrink: true }} 
                                         value={staffNote} onChange={(e) => setStaffNote(e.target.value)} />
                                    </Grid>
                                    <Grid size={{ xs: 12, md: 4 }}>
                                        <Box sx={{ display: "flex", alignItems: "center", gap: 1, justifyContent: "flex-end" }}>
                                            <Button
                                                variant="outlined"
                                        
                                                className="button-variant-outlined"
                                                onClick={handleCancel}>Cancel</Button>
                                            <Button
                                                variant="contained"
                                                className="button-variant-contained"
                                                onClick={handleSave}>Confirm & Submit
                                            </Button>
                                        </Box>
                                    </Grid>
                                </Grid>
                            </Grid2>
                        </>
                    )}
                </Paper>
            </Box>
            <AddProductDialog
                open={openAddDialog}
                onClose={closeAddProduct}
                customerId={selectedCustomer?.id}
                orderChannel={orderChannel}
                onSave={handleAddProductSave}
            />

            <PromotionsDialog
                open={promotionsModalOpen}
                onClose={handleClosePromotionsModal}
                appliedPromotions={displayAppliedPromotions}
                availablePromotions={displayAvailablePromotions}
                isLoading={isLoadingCartPromotions || checkPromotionMutation.isPending}
                isActionPending={checkPromotionMutation.isPending}
                totalApplicable={totalApplicable}
                onOpenDrawer={handleOpenPromotionDrawer}
                onRemovePromotion={handleRemovePromotion}
                onApplyPromotion={handleApplyPromotion}
            />

            <PromotionDetailsDrawerEditEpos
                open={promotionDrawerOpen}
                onClose={handleClosePromotionDrawer}
                promotion={selectedPromotion}
                cartItems={cartItems}
                customerId={customerId}
                orderId={orderId}
                onCheckPromotionGetSwap={handleCheckPromotionGetSwap}
                onCartUpdate={(updatedItems, updatedTotals) => {
                    promotionCheckEpochRef.current += 1;
                    const nextItems = Array.isArray(updatedItems)
                        ? updatedItems.map((item) => {
                            const raw =
                                item.allot_qty !== undefined && item.allot_qty !== null && item.allot_qty !== ""
                                    ? item.allot_qty
                                    : item.qty !== undefined && item.qty !== null && item.qty !== ""
                                        ? item.qty
                                        : item.quantity;
                            const num = parseFloat(raw);
                            const n = Number.isFinite(num) ? num : parseInt(String(raw), 10) || 0;
                            const str = String(n);
                            return { ...item, quantity: n, qty: str, allot_qty: str };
                        })
                        : updatedItems;
                    cartItemsRef.current = nextItems;
                    setCartItems(nextItems);
                    if (updatedTotals) {
                        setApiTotals(updatedTotals);
                    }
                }}
                onQueuePromotionCheck={() => {
                    schedulePromotionCheck();
                }}
            />

            <ConfirmDialog
                open={saveConfirmDialog}
                onClose={handleCloseSaveConfirmDialog}
                title="Confirm Order"
                content={
                    <Typography>
                        Do you really want to submit this order?
                    </Typography>
                }
                action={
                    <Button
                        variant="contained"

                        onClick={handleConfirmSave}
                        disabled={updateInvoiceMutation.isPending}
                        sx={{
                            ml: 1,
                            backgroundColor: "#F8991D",
                            "&:hover": {
                                backgroundColor: "#F8991D",
                                boxShadow: "none",
                            },
                        }}
                    >
                        {updateInvoiceMutation.isPending ? "Submitting..." : "Confirm"}
                    </Button>
                }
            />

            <ConfirmDialog
                open={cancelConfirmDialog}
                onClose={handleCloseCancelConfirmDialog}
                title="Discard Changes"
                content={
                    <Typography>
                        Do you really want to cancel and discard all changes?
                    </Typography>
                }
                action={
                    <Button
                        variant="contained"
                        color="error"
                        onClick={handleConfirmCancel}
                        sx={{ ml: 1 }}
                    >
                        Discard
                    </Button>
                }
            />

            <ConfirmDialog
                open={deleteItemDialog.open}
                onClose={handleCloseDeleteItemDialog}
                title="Delete Item"
                content="Do you really want to delete this item?"
                action={
                    <Button
                        variant="contained"
                        color="error"
                        onClick={handleConfirmDeleteItem}
                        sx={{ ml: 1 }}
                    >
                        Delete
                    </Button>
                }
            />

            <SalesStockLimitDialog
                open={stockLimitDialog.open}
                onClose={closeStockLimitDialog}
                productName={stockLimitDialog.productName}
                productCode={stockLimitDialog.productCode}
                maxQty={stockLimitDialog.maxQty}
                message={stockLimitDialog.message || ""}
                plainTitle={stockLimitDialog.plainTitle}
            />

            <Snackbar
                open={snackbar.open}
                autoHideDuration={4000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
                <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} variant="filled" sx={{ width: '100%' }}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </>
    );
};

export default EditNewEpos;


